import { useEffect, useState } from "react";

/**
 * Returns false on the first paint, then true a beat later (after the browser is idle /
 * the next frame). Use it to defer mounting non-critical, JS-heavy chrome — the header,
 * footer, sticky bar, and the below-the-fold page body — until AFTER the above-the-fold
 * hero has painted.
 *
 * Why: those pieces dynamically import framer-motion (a ~900ms evaluate under mobile CPU
 * throttling) and other chunks. If they mount immediately they contend with the entry +
 * React for bandwidth and the main thread, delaying the hero's Largest Contentful Paint.
 * Deferring them lets the eager hero paint first, then streams the rest in.
 */
export function useDeferredMount(): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const raf = requestAnimationFrame(() => {
      const win = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      if (typeof win.requestIdleCallback === "function") {
        idleId = win.requestIdleCallback(() => setReady(true), { timeout: 400 });
      } else {
        timeoutId = window.setTimeout(() => setReady(true), 1);
      }
    });

    return () => {
      cancelAnimationFrame(raf);
      const win = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (idleId !== undefined && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  return ready;
}
